import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, Trash2, Pencil, Save, X, Plus } from "lucide-react";
import { riskLevel, riskLevelLabel, riskLevelClasses } from "@/lib/risk";

function RiskBadge({ level }) {
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${riskLevelClasses(level)}`}>
      {riskLevelLabel(level)}
    </span>
  );
}

/**
 * PericoloCard — visualizza/edita un pericolo dello Step 5.
 * Campi editabili: pericolo, descrizione, P, D (R e livello ricalcolati automaticamente),
 * misure_prevenzione (array), normativa, miglioramenti_proposti (array),
 * mansioni_esposte (multi-select da step4_mansioni).
 */
export default function PericoloCard({ item, index, onSave, onRemove, mansioniOptions = [] }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item);

  const startEdit = () => {
    setDraft(JSON.parse(JSON.stringify(item)));
    setEditing(true);
  };
  const cancel = () => {
    setDraft(item);
    setEditing(false);
  };
  const save = () => {
    onSave({ ...draft, _edited: true });
    setEditing(false);
  };

  const setField = (k, v) => {
    const next = { ...draft, [k]: v };
    if (k === "P" || k === "D") {
      const P = Number(next.P) || 0;
      const D = Number(next.D) || 0;
      next.R = String(P * D);
      next.livello = riskLevel(P, D);
    }
    setDraft(next);
  };

  const updateStr = (key, i, val) => {
    const arr = [...(draft[key] || [])];
    arr[i] = val;
    setDraft({ ...draft, [key]: arr });
  };
  const addStr = (key) => setDraft({ ...draft, [key]: [...(draft[key] || []), ""] });
  const removeStr = (key, i) =>
    setDraft({ ...draft, [key]: (draft[key] || []).filter((_, idx) => idx !== i) });

  const view = editing ? draft : item;
  const lvl = view.livello || riskLevel(view.P, view.D);

  return (
    <div className="border border-border rounded-md p-4 bg-card" data-testid={`s5-card-${index}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          {editing ? (
            <Input value={draft.pericolo || ""} onChange={(e) => setField("pericolo", e.target.value)} />
          ) : (
            <div className="flex items-center gap-2">
              <h3 className="font-semibold">{view.pericolo}</h3>
              <RiskBadge level={lvl} />
              <span className="text-[11px] text-muted-foreground">
                P={view.P || "?"} · D={view.D || "?"} · R={view.R || "?"}
              </span>
              {item._edited && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                  Modificato
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          {editing ? (
            <>
              <Button size="sm" className="h-8 rounded-full" onClick={save} data-testid={`s5-save-${index}`}>
                <Save className="w-3.5 h-3.5 mr-1" /> Salva
              </Button>
              <Button size="sm" variant="ghost" className="h-8" onClick={cancel} data-testid={`s5-cancel-${index}`}>
                <X className="w-3.5 h-3.5" />
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-8 rounded-full"
                onClick={startEdit}
                data-testid={`s5-edit-${index}`}
              >
                <Pencil className="w-3.5 h-3.5 mr-1" /> Modifica
              </Button>
              <button
                onClick={onRemove}
                className="text-muted-foreground hover:text-destructive p-1"
                data-testid={`s5-remove-${index}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {editing ? (
        <div className="mt-3 space-y-2">
          <div>
            <Label className="text-[10px]">Descrizione</Label>
            <Textarea rows={2} value={draft.descrizione || ""} onChange={(e) => setField("descrizione", e.target.value)} />
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div>
              <Label className="text-[10px]">P (1-4)</Label>
              <Input type="number" min={1} max={4} value={draft.P || 1} onChange={(e) => setField("P", e.target.value)} />
            </div>
            <div>
              <Label className="text-[10px]">D (1-4)</Label>
              <Input type="number" min={1} max={4} value={draft.D || 1} onChange={(e) => setField("D", e.target.value)} />
            </div>
            <div>
              <Label className="text-[10px]">R (P×D)</Label>
              <Input value={draft.R || ""} readOnly className="bg-muted/40" />
            </div>
            <div>
              <Label className="text-[10px]">Livello</Label>
              <Input value={riskLevelLabel(draft.livello)} readOnly className="bg-muted/40" />
            </div>
          </div>
          <div>
            <Label className="text-[10px]">Normativa</Label>
            <Input value={draft.normativa || ""} onChange={(e) => setField("normativa", e.target.value)} />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[10px]">Misure di prevenzione</Label>
              <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => addStr("misure_prevenzione")}>
                <Plus className="w-3 h-3 mr-0.5" /> aggiungi
              </Button>
            </div>
            <div className="space-y-1 mt-1">
              {(draft.misure_prevenzione || []).map((m, i) => (
                <div key={`mp-${i}`} className="grid grid-cols-12 gap-2 items-center">
                  <Input
                    className="col-span-11"
                    value={m}
                    onChange={(e) => updateStr("misure_prevenzione", i, e.target.value)}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-9 w-9 p-0 col-span-1"
                    onClick={() => removeStr("misure_prevenzione", i)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[10px]">Miglioramenti proposti</Label>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 text-xs"
                onClick={() => addStr("miglioramenti_proposti")}
              >
                <Plus className="w-3 h-3 mr-0.5" /> aggiungi
              </Button>
            </div>
            <div className="space-y-1 mt-1">
              {(draft.miglioramenti_proposti || []).map((m, i) => (
                <div key={`mig-${i}`} className="grid grid-cols-12 gap-2 items-center">
                  <Input
                    className="col-span-11"
                    value={m}
                    onChange={(e) => updateStr("miglioramenti_proposti", i, e.target.value)}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-9 w-9 p-0 col-span-1"
                    onClick={() => removeStr("miglioramenti_proposti", i)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          {/* Mansioni esposte — multi-select da step4_mansioni */}
          <div>
            <Label className="text-[10px]">Mansioni esposte al pericolo</Label>
            {mansioniOptions.length === 0 ? (
              <p className="text-[11px] italic text-muted-foreground mt-1">
                Nessuna mansione ancora inserita nello Step 4. Torna allo Step 4 per aggiungerle.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid={`s5-mansioni-esposte-${index}`}>
                {mansioniOptions.map((m) => {
                  const isSel = (draft.mansioni_esposte || []).includes(m);
                  return (
                    <button
                      type="button"
                      key={`me-${m}`}
                      onClick={() => {
                        const cur = draft.mansioni_esposte || [];
                        const next = isSel ? cur.filter((x) => x !== m) : [...cur, m];
                        setDraft({ ...draft, mansioni_esposte: next });
                      }}
                      className={`text-xs px-2.5 py-1 rounded-full border transition ${
                        isSel
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background text-foreground border-input hover:bg-muted"
                      }`}
                      data-testid={`s5-me-toggle-${index}-${m}`}
                    >
                      {isSel && "✓ "}{m}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {view.descrizione && <p className="text-sm mt-2">{view.descrizione}</p>}
          {view.misure_prevenzione?.length > 0 && (
            <div className="mt-3 text-sm">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Misure di prevenzione</div>
              <ul className="mt-1 space-y-0.5">
                {view.misure_prevenzione.map((m, i) => (
                  <li key={`v-mp-${i}`}>• {m}</li>
                ))}
              </ul>
            </div>
          )}
          {view.miglioramenti_proposti?.length > 0 && (
            <div className="mt-3 text-sm border border-amber-200 bg-amber-50/40 rounded p-2">
              <div className="text-[11px] uppercase tracking-widest text-amber-700 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Miglioramenti proposti
              </div>
              <ul className="mt-1 space-y-0.5">
                {view.miglioramenti_proposti.map((m, i) => (
                  <li key={`v-mig-${i}`}>• {m}</li>
                ))}
              </ul>
            </div>
          )}
          {view.mansioni_esposte?.length > 0 && (
            <div className="mt-3 text-sm">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Mansioni esposte</div>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {view.mansioni_esposte.map((m, i) => (
                  <span
                    key={`me-view-${m}-${i}`}
                    className="text-xs px-2 py-0.5 rounded bg-sky-100 text-sky-800 border border-sky-200"
                  >
                    {m}
                  </span>
                ))}
              </div>
            </div>
          )}
          {view.normativa && (
            <div className="mt-2 text-[11px] text-muted-foreground">Rif: {view.normativa}</div>
          )}
        </>
      )}
    </div>
  );
}
